import { useState, useEffect } from 'react';
import { Box, Divider, Flex, HStack } from '@chakra-ui/react';
import { isEmpty } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { axios } from '@/services/axios';
import SplitJoinStatus from './SplitJoinStatus';
import walkthroughIds from '../walkthroughIds';
import {
    Modal,
    ModalOverlay,
    ModalContent,
    ModalHeader,
    ModalBody,
    ModalFooter,
    Button,
    FormControl,
    FormLabel,
    Text,
    Skeleton,
} from '@/components/v4';

const SplitJoinBlock = ({ isOpen, onClose, id, state, setId }) => {
    const dispatch = useDispatch();
    const [isLoading, setIsLoading] = useState(true);
    const [splitOptions, setSplitOptions] = useState([]);
    const [joinOptions, setJoinOptions] = useState([]);

    const [numberBlock, setNumberBlock] = useState(null);
    const [bSizeWithIds, setBSizeWithIds] = useState(new Map());
    const [submitState, setSubmitState] = useState(state);

    const [errorMessage, setErrorMessage] = useState(null);
    const [totalCount, setTotalCount] = useState(0);
    const [failedCount, setFailedCount] = useState(0);
    const [successCount, setSuccessCount] = useState(0);

    const [selectedSplitOption, setSelectedSplitOption] = useState(null);
    const [selectedJoinOption, setSelectedJoinOption] = useState(null);

    const { darkMode } = useSelector((state) => state.settings);
    const textColor = darkMode ? 'white' : 'gray.800';

    const { requiredScope } = useSelector((state) => state.login);
    const {
        ufSplitBlocksizeButton,
        ufJoinBlocksizeButton,
        ufCloseButton,
        ufBackButton,
        ufSubmitButton,
    } = walkthroughIds;

    useEffect(() => {
        if (numberBlock) {
            setErrorMessage(null);
            // if pending update then do not allow to split as backend will send back the same
            if (!numberBlock.pendingUpdate) {
                let tempSplitOptions = [];
                // value corresponds to the times it
                // would iterate to create the new number block
                if (numberBlock.blockSize === 5) {
                    tempSplitOptions.push({ value: '1' });
                } else if (numberBlock.blockSize === 10) {
                    tempSplitOptions.push({ value: '1' }, { value: '5' });
                } else if (numberBlock.blockSize === 100) {
                    tempSplitOptions.push(
                        { value: '1' },
                        { value: '5' },
                        { value: '10' },
                    );
                }
                setSplitOptions(tempSplitOptions);
            }

            // if number block if less than 100, then call to get options
            if (numberBlock.blockSize != 100) {
                axios
                    .get('/numberblock/join/' + id, {
                        headers: {
                            'X-RequestScope': requiredScope,
                        },
                    })
                    .then((res) => {
                        const bSizeWithIds = new Map();
                        const tempJoin = [];
                        for (const key in res.data.options) {
                            tempJoin.push({ value: key });
                            if (Object.hasOwn(res.data.options, key)) {
                                bSizeWithIds.set(key, res.data.options[key]);
                            }
                        }
                        setJoinOptions(tempJoin);
                        setBSizeWithIds(bSizeWithIds);
                        setIsLoading(false);
                    })
                    .catch((err) => {
                        console.log(err);
                        setErrorMessage(err.response.data.message);
                        setSubmitState('idle');
                        setIsLoading(false);
                    });
            } else setIsLoading(false);
        } else {
            setSubmitState('idle');
            setIsLoading(false);
        }
    }, [numberBlock]);

    useEffect(() => {
        if (id) {
            setIsLoading(true);
            setErrorMessage(null);
            axios
                .get('/numberblock/' + id, {
                    headers: {
                        'X-RequestScope': requiredScope,
                    },
                })
                .then((res) => {
                    if (res.data.pendingUpdate) {
                        setErrorMessage(
                            'The Number Block still has changes pending. Please wait for the sync process and try again.',
                        );
                        setSubmitState('idle');
                        setIsLoading(false);
                        return;
                    }
                    setNumberBlock(res.data);
                })
                .catch((err) => {
                    console.log(err);
                    setErrorMessage(err.response.data.message);
                    reset();
                    setSubmitState('idle');
                    setIsLoading(false);
                });
        }
    }, [id]);

    const handleSplitChange = (event) => {
        setSubmitState('idle');
        if (!isEmpty(selectedJoinOption)) {
            setSelectedJoinOption('');
        }
        if (event.target.value === selectedSplitOption) {
            setSelectedSplitOption('');
        } else {
            setSelectedSplitOption(event.target.value);
        }
    };

    const handleJoinChange = (event) => {
        setSubmitState('idle');
        if (!isEmpty(selectedSplitOption)) {
            setSelectedSplitOption('');
        }
        if (event.target.value === selectedJoinOption) {
            setSelectedJoinOption('');
        } else {
            setSelectedJoinOption(event.target.value);
        }
    };

    // create new number block after delete old number blocks
    const processBlocks = async (bOpt, processType) => {
        // join process
        if (processType) {
            let newNumberBlock = {
                companyID: numberBlock.companyID,
                partnerID: numberBlock.partnerID,
                wholesalerID: numberBlock.wholesalerID,
                carrierID: numberBlock.carrierID,
                inboundCarrierID: numberBlock.inboundCarrierID,
                outboundCarrierID: numberBlock.outboundCarrierID,
                blockSize: bOpt,
                publicPool: numberBlock.publicPool,
                // need to add first and last number
                first: numberBlock.first,
                last: (
                    parseInt(numberBlock.first) + parseInt(bOpt - 1)
                ).toString(),
            };

            // create new single number block
            try {
                await axios.post('/numberblock', newNumberBlock);
                setSuccessCount((successCount) => successCount + 1);
            } catch (err) {
                setFailedCount((failedCount) => failedCount + 1);
                setErrorMessage(err.response.data.message);
                reset();
                setSubmitState('idle');
            }
        } else {
            const iterateSize = parseInt(numberBlock.blockSize / bOpt);

            for (var i = 0; i < iterateSize; i++) {
                let firstInc = i * parseInt(bOpt);
                let lastInc = firstInc + (parseInt(bOpt) - 1);
                let newNumberBlock = {
                    companyID: numberBlock?.companyID,
                    partnerID: numberBlock?.partnerID,
                    wholesalerID: numberBlock?.wholesalerID,
                    carrierID: numberBlock?.carrierID,
                    inboundCarrierID: numberBlock?.inboundCarrierID,
                    outboundCarrierID: numberBlock?.outboundCarrierID,
                    blockSize: bOpt,
                    publicPool: numberBlock?.publicPool,
                    first: (parseInt(numberBlock.first) + firstInc).toString(),
                    last: (parseInt(numberBlock.first) + lastInc).toString(),
                };

                // create new individual number block of selected block size
                await axios
                    .post('/numberblock', newNumberBlock)
                    .then((result) => {
                        setSuccessCount((successCount) => successCount + 1);
                    })
                    .catch((error) => {
                        setFailedCount(i + 1);
                        setErrorMessage(error.response.data.message);
                        reset();
                        setSubmitState('idle');
                        return;
                    });
            }
        }

        return;
    };

    // submit button
    const handleSubmit = async (e) => {
        // prevent refreshing the page
        e.preventDefault();
        // restrict from sending empty block size
        setErrorMessage(null);
        setSubmitState('idle');
        setTotalCount(0);
        setSuccessCount(0);
        setFailedCount(0);

        const selectedBlockSize = parseInt(
            selectedJoinOption || selectedSplitOption,
        );
        const processType = selectedJoinOption ? true : false;
        const deleteBlockIds = bSizeWithIds.get(selectedBlockSize.toString());

        // calculate total count option based on blocksize
        // + 1 is for the the new number block
        if (selectedJoinOption) {
            setTotalCount(deleteBlockIds.length + 1);

            setSubmitState('processing');

            for (let i = 0; i < deleteBlockIds.length; i++) {
                try {
                    await axios.delete(`/numberblock/${deleteBlockIds[i]}`);
                    setSuccessCount(i + 1);
                } catch (err) {
                    setErrorMessage(err.response?.data?.message);
                    reset();
                    setSubmitState('idle');
                    return;
                }
                setSuccessCount(i + 1);
            }
        } else {
            // total request count
            setTotalCount(
                parseInt(numberBlock.blockSize / selectedBlockSize) + 1,
            );

            setSubmitState('processing');

            try {
                await axios.delete(`/numberblock/${numberBlock.id}`);
                setSuccessCount(1);
            } catch (err) {
                setErrorMessage(err.response?.data?.message);
                reset();
                setSubmitState('idle');
                return;
            }
        }
        await processBlocks(selectedBlockSize, processType);
    };

    // back button
    const closeModal = () => {
        setSubmitState('idle');
        setId(null);
        setIsLoading(true);
        setErrorMessage(null);
        reset();
        onClose();
    };

    // reset close
    const handleClose = () => {
        closeModal();
        dispatch({
            type: 'RESET_TABLE',
        });
    };

    const reset = () => {
        setNumberBlock(null);
        setSelectedJoinOption(null);
        setSelectedSplitOption(null);
        setJoinOptions([]);
        setSplitOptions([]);
    };

    // Fires an event-shaped object so handleSplitChange/handleJoinChange
    // (which read event.target.value) don't need to change.
    const toggleEvent = (value) => ({ target: { value } });

    return (
        <Modal isOpen={isOpen} onClose={onClose} size="xl">
            <ModalOverlay />
            <ModalContent>
                <ModalHeader>
                    Number Block{' '}
                    {isLoading ? (
                        <Skeleton
                            display="inline-block"
                            width="80px"
                            height="20px"
                        />
                    ) : numberBlock ? (
                        `: +${numberBlock?.first}`
                    ) : (
                        ''
                    )}
                </ModalHeader>
                <ModalBody>
                    {errorMessage && (
                        <Box mb={2}>
                            <Text color="red.500">{errorMessage}</Text>
                        </Box>
                    )}
                    {submitState === 'idle' && (
                        <>
                            <Flex gap={4} align="flex-start">
                                {/* Split */}
                                <Box flex="1">
                                    <Text
                                        fontSize="lg"
                                        fontWeight="bold"
                                        mb={2}
                                        color={textColor}>
                                        Split
                                    </Text>
                                    {isLoading ? (
                                        Array(3)
                                            .fill(1)
                                            .map((_, index) => (
                                                <Skeleton
                                                    key={index}
                                                    height="40px"
                                                    mt={2}
                                                />
                                            ))
                                    ) : (
                                        <>
                                            <FormControl>
                                                <FormLabel>
                                                    Block size
                                                </FormLabel>
                                                <HStack
                                                    spacing={2}
                                                    data-walkthroughid={
                                                        ufSplitBlocksizeButton
                                                    }
                                                    height="48px"
                                                    alignItems="center">
                                                    {splitOptions.map(
                                                        (item, index) => (
                                                            <Button
                                                                key={index}
                                                                size="sm"
                                                                width="60px"
                                                                variant={
                                                                    selectedSplitOption ===
                                                                    item.value
                                                                        ? 'solid'
                                                                        : 'outline'
                                                                }
                                                                onClick={() =>
                                                                    handleSplitChange(
                                                                        toggleEvent(
                                                                            item.value,
                                                                        ),
                                                                    )
                                                                }>
                                                                {item.value}
                                                            </Button>
                                                        ),
                                                    )}
                                                </HStack>
                                            </FormControl>
                                            {selectedSplitOption &&
                                                numberBlock && (
                                                    <Text
                                                        fontSize="sm"
                                                        color="gray.500"
                                                        mt={2}>
                                                        This will create{' '}
                                                        {parseInt(
                                                            numberBlock?.blockSize /
                                                                selectedSplitOption,
                                                        )}{' '}
                                                        individual{' '}
                                                        {selectedSplitOption}{' '}
                                                        number block starting at
                                                        +{numberBlock?.first}{' '}
                                                        and ending at +
                                                        {numberBlock?.last}
                                                    </Text>
                                                )}
                                        </>
                                    )}
                                </Box>
                                <Divider
                                    orientation="vertical"
                                    h="auto"
                                    alignSelf="stretch"
                                />
                                {/* Join */}
                                <Box flex="1">
                                    <Text
                                        fontSize="lg"
                                        fontWeight="bold"
                                        mb={2}
                                        color={textColor}>
                                        Join
                                    </Text>
                                    {isLoading ? (
                                        Array(3)
                                            .fill(1)
                                            .map((_, index) => (
                                                <Skeleton
                                                    key={index}
                                                    height="40px"
                                                    mt={2}
                                                />
                                            ))
                                    ) : (
                                        <>
                                            <FormControl>
                                                <FormLabel>
                                                    Block size
                                                </FormLabel>
                                                <HStack
                                                    spacing={2}
                                                    data-walkthorughid={
                                                        ufJoinBlocksizeButton
                                                    }
                                                    height="48px"
                                                    alignItems="center">
                                                    {joinOptions.map(
                                                        (item, index) => (
                                                            <Button
                                                                key={index}
                                                                size="sm"
                                                                width="60px"
                                                                variant={
                                                                    selectedJoinOption ===
                                                                    item.value
                                                                        ? 'solid'
                                                                        : 'outline'
                                                                }
                                                                onClick={() =>
                                                                    handleJoinChange(
                                                                        toggleEvent(
                                                                            item.value,
                                                                        ),
                                                                    )
                                                                }>
                                                                {item.value}
                                                            </Button>
                                                        ),
                                                    )}
                                                </HStack>
                                            </FormControl>
                                            {selectedJoinOption &&
                                                numberBlock && (
                                                    <Text
                                                        fontSize="sm"
                                                        color="gray.500"
                                                        mt={2}>
                                                        This will create a new
                                                        block +
                                                        {numberBlock?.first} - +
                                                        {parseInt(
                                                            numberBlock?.first,
                                                        ) +
                                                            parseInt(
                                                                selectedJoinOption -
                                                                    1,
                                                            )}
                                                    </Text>
                                                )}
                                        </>
                                    )}
                                </Box>
                            </Flex>
                            <Box mt={2}>
                                {(selectedSplitOption ||
                                    selectedJoinOption) && (
                                    <>
                                        <Text
                                            fontWeight="semibold"
                                            fontSize="sm"
                                            color={textColor}>
                                            Please note:
                                        </Text>
                                        <Box
                                            as="ul"
                                            color={textColor}
                                            sx={{ marginLeft: '16px' }}>
                                            <Box as="li" fontSize="13px">
                                                {selectedSplitOption
                                                    ? 'Splitting'
                                                    : 'Joining'}{' '}
                                                a number block requires a valid
                                                number block rate for the
                                                selected block size.
                                            </Box>
                                            <Box as="li" fontSize="13px">
                                                {selectedSplitOption
                                                    ? 'Splitting'
                                                    : 'Joining'}{' '}
                                                a number block may incur
                                                charges/fees.
                                            </Box>
                                            <Box as="li" fontSize="13px">
                                                You are responsible for the
                                                changes you are about to make
                                                through{' '}
                                                {selectedSplitOption
                                                    ? 'splitting'
                                                    : 'joining'}{' '}
                                                number block.
                                            </Box>
                                        </Box>
                                    </>
                                )}
                            </Box>
                        </>
                    )}
                    {submitState === 'processing' && (
                        <SplitJoinStatus
                            totalCount={totalCount}
                            successCount={successCount}
                            failedCount={failedCount}
                        />
                    )}
                </ModalBody>
                <ModalFooter>
                    {submitState === 'idle' && (
                        <HStack spacing={3}>
                            <Button onClick={handleClose} variant="outline">
                                Close
                            </Button>
                            <Button
                                onClick={handleSubmit}
                                isDisabled={
                                    isEmpty(selectedJoinOption) &&
                                    isEmpty(selectedSplitOption)
                                }>
                                Submit
                            </Button>
                        </HStack>
                    )}
                    {submitState === 'processing' && (
                        <Button
                            onClick={handleClose}
                            isDisabled={
                                !(successCount + failedCount === totalCount)
                            }>
                            Back
                        </Button>
                    )}
                </ModalFooter>
            </ModalContent>
        </Modal>
    );
};

export default SplitJoinBlock;
